/** QuickTicketing — Liste des tickets : « mes tickets », ou tous (administrateur). */
import React, { useEffect, useMemo, useState } from 'react';
import { api, ago } from '../api/client.js';
import { StatusBadge, PriorityTag } from './Badges.jsx';

const OPEN = 'ouverts';
const QUEUE = 'file';

export default function TicketList({ me, meta, scope, navigate }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(scope === 'all' ? QUEUE : OPEN);
  const [app, setApp] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    setRows(null);
    api(`/tickets${scope === 'mine' ? '?scope=mine' : ''}`)
      .then((r) => alive && setRows(r))
      .catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [scope]);

  const closed = ['clos', 'rejete'];
  const groups = useMemo(() => {
    const g = [
      ...(scope === 'all' ? [{ id: QUEUE, label: 'File de traitement', test: (r) => meta.queue.includes(r.status) }] : []),
      { id: OPEN, label: 'Ouverts', test: (r) => !closed.includes(r.status) },
      ...meta.statuses.map((s) => ({ id: s.id, label: s.label, test: (r) => r.status === s.id })),
      { id: 'tous', label: 'Tous', test: () => true },
    ];
    return g.map((x) => ({ ...x, count: (rows || []).filter(x.test).length }));
  }, [rows, meta, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const g = groups.find((x) => x.id === filter) || groups[0];
    const needle = q.trim().toLowerCase();
    return (rows || [])
      .filter(g.test)
      .filter((r) => !app || r.app === app)
      .filter((r) => !needle || `${r.id} ${r.title} ${r.reporter}`.toLowerCase().includes(needle));
  }, [rows, groups, filter, app, q]);

  const appName = (id) => meta.apps.find((a) => a.id === id)?.name ?? id;

  return (
    <div className="page">
      <div className="row wrap page-head">
        <h2 className="grow">{scope === 'all' ? 'Tous les tickets' : 'Mes tickets'}</h2>
        <input className="search" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={app} onChange={(e) => setApp(e.target.value)}>
          <option value="">Toutes les applications</option>
          {meta.apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <button className="primary" onClick={() => navigate('/nouveau')}>+ Signaler un problème</button>
      </div>

      <div className="chips">
        {groups.filter((g) => g.count || g.id === filter || [OPEN, QUEUE, 'tous'].includes(g.id)).map((g) => (
          <button key={g.id} className={filter === g.id ? 'active' : ''} onClick={() => setFilter(g.id)}>
            {g.label} <span className="count">{g.count}</span>
          </button>
        ))}
      </div>

      {error && <div className="error-line">{error}</div>}
      {!rows && !error && <div className="empty"><span className="spinner" /> Chargement…</div>}
      {rows && !shown.length && (
        <div className="empty card">
          {rows.length ? 'Aucun ticket dans cette sélection.' : scope === 'mine'
            ? 'Vous n\'avez signalé aucun problème. Un souci dans une application de la suite ? « + Signaler un problème ».'
            : 'Aucun ticket pour l\'instant.'}
        </div>
      )}
      {shown.length > 0 && (
        <table className="tickets">
          <thead>
            <tr>
              <th>Ticket</th><th>Titre</th><th>Application</th><th>État</th>
              {me.admin && <th>Prio.</th>}
              {scope === 'all' && <th>Demandeur</th>}
              <th>Mis à jour</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/t/${r.id}`)} className={r.unread ? 'unread' : ''}>
                <td className="mono">{r.unread && <span className="dot" title="Nouvelle activité" />}{r.id}</td>
                <td className="title-cell">
                  {r.title}
                  {(r.comments > 0 || r.files > 0) && <span className="muted"> {r.comments > 0 && `💬 ${r.comments}`} {r.files > 0 && `📎 ${r.files}`}</span>}
                </td>
                <td>{appName(r.app)}</td>
                <td><StatusBadge status={r.status} meta={meta} /></td>
                {me.admin && <td><PriorityTag priority={r.priority} /></td>}
                {scope === 'all' && <td>{r.reporter}</td>}
                <td className="muted nowrap">{ago(r.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
